import {
  datePl,
  FLEET_COST_CATEGORIES,
  type FleetCostCategory,
  type FleetCostDto,
  type FleetEventDto,
  type FleetReportDto,
  REPORT_CATEGORIES,
  SENSITIVE_DOC_CATEGORIES,
  todayInWarsaw,
  VEHICLE_DOC_CATEGORIES,
  type VehicleDocCategory,
  type VehicleDocDto,
  type VehicleDto,
} from '@bqcrm/shared';
import { ActionIcon, Anchor, Badge, Button, FileButton, Group, Modal, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Table, Text, TextInput, Timeline, Tooltip } from '@mantine/core';
import { IconDownload, IconLock, IconPlus, IconTrash, IconUpload } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../../api';
import { ask } from '../../../confirm';
import { BlocksUseBadge, FleetCostKindBadge, FleetReportStatusBadge } from '../../../components/Badges';
import { Money, MoneyInput } from '../../../components/Money';
import { QueryState } from '../../../components/QueryState';
import { EmptyState, SectionCard } from '../../../components/ui';
import { formatDateTime, qtyPl } from '../../../format';
import { notifyError, notifyOk } from '../../../notify';
import { apiForm, ReportFormModal, useFleetInvalidate } from '../common';

// ── zgłoszenia pojazdu ──

export function VehicleReportsTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'reports', v.id], queryFn: () => api<FleetReportDto[]>(`/fleet/reports?vehicleId=${v.id}`) });
  const [adding, setAdding] = useState(false);
  return (
    <SectionCard
      title="Zgłoszenia"
      padding={0}
      actions={
        v.can.manage &&
        v.status === 'aktywny' && (
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
            Zgłoś problem
          </Button>
        )
      }
    >
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data?.length ? (
          <Table.ScrollContainer minWidth={700}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Numer</Table.Th>
                  <Table.Th>Opis</Table.Th>
                  <Table.Th>Zgłaszający</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {q.data.map((r) => (
                  <Table.Tr key={r.id} style={r.blocksUse && r.status !== 'zakonczone' && r.status !== 'odrzucone' ? { backgroundColor: 'var(--mantine-color-red-light)' } : undefined}>
                    <Table.Td>
                      <Anchor component={Link} to={`/flota/zgloszenia?zgloszenie=${r.id}`} size="sm" fw={600}>
                        {r.number}
                      </Anchor>
                      <Text size="xs" c="dimmed">
                        {REPORT_CATEGORIES[r.category]}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={2}>
                        {r.description}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{r.reporter.name}</Text>
                      <Text size="xs" c="dimmed">
                        {formatDateTime(r.createdAt)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {r.blocksUse && <BlocksUseBadge size="xs" />}
                        <FleetReportStatusBadge status={r.status} />
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : (
          <EmptyState compact title="Brak zgłoszeń dla tego pojazdu" />
        )}
      </QueryState>
      {adding && <ReportFormModal vehicles={[{ id: v.id, label: v.plate }]} initialVehicleId={v.id} onClose={() => setAdding(false)} />}
    </SectionCard>
  );
}

// ── koszty ──

function CostModal({ v, onClose }: { v: VehicleDto; onClose: () => void }) {
  const invalidate = useFleetInvalidate();
  const [kind, setKind] = useState<'reczny' | 'przewidywany'>('reczny');
  const [category, setCategory] = useState<FleetCostCategory>('serwis');
  const [costDate, setCostDate] = useState(todayInWarsaw());
  const [netGr, setNetGr] = useState<number | null>(null);
  const [grossGr, setGrossGr] = useState<number | null>(null);
  const [liters, setLiters] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const save = useMutation({
    mutationFn: () => api(`/fleet/vehicles/${v.id}/costs`, { method: 'POST', body: { kind, category, costDate, netGr, grossGr: grossGr ?? netGr, liters: category === 'paliwo' ? liters : null, note } }),
    onSuccess: () => {
      notifyOk('Dodano koszt');
      invalidate();
      onClose();
    },
    onError: notifyError,
  });
  return (
    <Modal opened onClose={onClose} title="Nowy koszt pojazdu">
      <Stack gap="sm">
        <SegmentedControl
          value={kind}
          onChange={(x) => setKind(x as 'reczny' | 'przewidywany')}
          data={[
            { value: 'reczny', label: 'Ręczny (poniesiony)' },
            { value: 'przewidywany', label: 'Przewidywany' },
          ]}
        />
        <Text size="xs" c="dimmed">
          Koszty z faktur przypisuje się w Finansach → Faktury zakupu. Przewidywane nie są doliczane do sum w raportach.
        </Text>
        <SimpleGrid cols={2} spacing="sm">
          <Select label="Kategoria" data={Object.entries(FLEET_COST_CATEGORIES).map(([value, label]) => ({ value, label }))} value={category} onChange={(x) => x && setCategory(x as FleetCostCategory)} allowDeselect={false} />
          <TextInput label="Data kosztu" type="date" required value={costDate} onChange={(e) => setCostDate(e.currentTarget.value)} />
          <MoneyInput label="Kwota netto" required valueGr={netGr} onChangeGr={setNetGr} />
          <MoneyInput label="Kwota brutto" description="puste — jak netto" valueGr={grossGr} onChangeGr={setGrossGr} />
          {category === 'paliwo' && <NumberInput label="Litry" min={0} decimalScale={2} decimalSeparator="," value={liters ?? ''} onChange={(x) => setLiters(typeof x === 'number' ? x : null)} />}
        </SimpleGrid>
        <TextInput label="Opis" value={note} onChange={(e) => setNote(e.currentTarget.value)} />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Anuluj
          </Button>
          <Button disabled={netGr === null || !costDate} loading={save.isPending} onClick={() => save.mutate()}>
            Dodaj koszt
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

/** Koszty i paliwo pojazdu: z faktur, ręczne i przewidywane — osobno. */
export function CostsTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'costs'], queryFn: () => api<FleetCostDto[]>(`/fleet/vehicles/${v.id}/costs`) });
  const invalidate = useFleetInvalidate();
  const [adding, setAdding] = useState(false);
  const remove = useMutation({
    mutationFn: (id: string) => api(`/fleet/costs/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      notifyOk('Usunięto koszt');
      invalidate();
    },
    onError: notifyError,
  });
  const rows = q.data ?? [];
  const sum = (k: string) => rows.filter((r) => r.kind === k && !r.invoice?.rejected).reduce((a, r) => a + r.netGr, 0);
  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <SectionCard title="Z faktur (netto)">
          <Text fw={700} size="lg">
            <Money gr={sum('faktura')} />
          </Text>
        </SectionCard>
        <SectionCard title="Ręczne (netto)">
          <Text fw={700} size="lg">
            <Money gr={sum('reczny')} />
          </Text>
        </SectionCard>
        <SectionCard title="Przewidywane (netto, poza sumą)">
          <Text fw={700} size="lg" c="dimmed">
            <Money gr={sum('przewidywany')} />
          </Text>
        </SectionCard>
      </SimpleGrid>
      <SectionCard
        title="Koszty"
        padding={0}
        actions={
          <Group gap="xs">
            <Button size="xs" variant="subtle" component={Link} to={`/flota/koszty`}>
              Raport kosztów
            </Button>
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAdding(true)}>
              Dodaj koszt
            </Button>
          </Group>
        }
      >
        <QueryState isLoading={q.isLoading} error={q.error}>
          {rows.length ? (
            <Table.ScrollContainer minWidth={820}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Data</Table.Th>
                    <Table.Th>Kategoria</Table.Th>
                    <Table.Th>Rodzaj</Table.Th>
                    <Table.Th>Opis / faktura</Table.Th>
                    <Table.Th ta="right">Litry</Table.Th>
                    <Table.Th ta="right">Netto</Table.Th>
                    <Table.Th ta="right">Brutto</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((c) => (
                    <Table.Tr key={c.id} style={c.invoice?.rejected || c.kind === 'przewidywany' ? { opacity: 0.6 } : undefined}>
                      <Table.Td>{datePl(c.costDate)}</Table.Td>
                      <Table.Td>{FLEET_COST_CATEGORIES[c.category]}</Table.Td>
                      <Table.Td>
                        <FleetCostKindBadge kind={c.kind} />
                      </Table.Td>
                      <Table.Td>
                        {c.invoice ? (
                          <>
                            <Text size="sm">
                              {c.invoice.number} · {c.invoice.sellerName}
                            </Text>
                            <Text size="xs" c="dimmed">
                              {c.invoice.lineLp !== null ? `poz. ${c.invoice.lineLp}: ${c.invoice.lineName}` : 'cała faktura'}
                              {c.invoice.rejected ? ' · faktura odrzucona — poza sumami' : ''}
                            </Text>
                          </>
                        ) : (
                          <Text size="sm">{c.note || '—'}</Text>
                        )}
                        {c.invoice && c.note && (
                          <Text size="xs" c="dimmed">
                            {c.note}
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td ta="right">{c.liters !== null ? qtyPl(c.liters) : '—'}</Table.Td>
                      <Table.Td ta="right">
                        <Money gr={c.netGr} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <Money gr={c.grossGr} />
                      </Table.Td>
                      <Table.Td ta="right">
                        <Tooltip label={c.kind === 'faktura' ? 'Usuń przypisanie faktury do pojazdu' : 'Usuń koszt'}>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            aria-label="Usuń koszt"
                            onClick={async () => (await ask(c.kind === 'faktura' ? 'Część faktury wróci do puli „do przypisania”.' : 'Koszt zniknie z raportów (zostaje w historii zdarzeń).', { title: 'Usunąć koszt?', confirm: 'Usuń', danger: true })) && remove.mutate(c.id)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          ) : (
            <EmptyState compact title="Brak kosztów" hint="Koszty z faktur przypisuje się w Finansach → Faktury zakupu → „Przypisz do pojazdu”; tu można dodać koszt ręczny albo przewidywany." />
          )}
        </QueryState>
      </SectionCard>
      {adding && <CostModal v={v} onClose={() => setAdding(false)} />}
    </Stack>
  );
}

// ── dokumenty ──

const sizeText = (b: number) => (b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${qtyPl(Math.round((b / 1024 / 1024) * 10) / 10)} MB`);

export function DocumentsTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'documents'], queryFn: () => api<VehicleDocDto[]>(`/fleet/vehicles/${v.id}/documents`) });
  const invalidate = useFleetInvalidate();
  const [file, setFile] = useState<File | null>(null);
  const [category, setCategory] = useState<VehicleDocCategory>('inne');
  const [description, setDescription] = useState('');
  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file!);
      form.append('category', category);
      form.append('description', description);
      return apiForm<VehicleDocDto[]>(`/fleet/vehicles/${v.id}/documents`, form);
    },
    onSuccess: () => {
      notifyOk('Dodano dokument');
      invalidate();
      setFile(null);
      setDescription('');
    },
    onError: notifyError,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/fleet/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      notifyOk('Usunięto dokument');
      invalidate();
    },
    onError: notifyError,
  });
  const categories = Object.entries(VEHICLE_DOC_CATEGORIES).filter(([k]) => v.can.costs || !SENSITIVE_DOC_CATEGORIES.includes(k as VehicleDocCategory));
  return (
    <SectionCard
      title="Dokumenty"
      padding={0}
      actions={
        v.can.manage && (
          <FileButton onChange={setFile}>
            {(props) => (
              <Button {...props} size="xs" leftSection={<IconUpload size={14} />}>
                Dodaj dokument
              </Button>
            )}
          </FileButton>
        )
      }
    >
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data?.length ? (
          <Table.ScrollContainer minWidth={700}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Plik</Table.Th>
                  <Table.Th>Rodzaj</Table.Th>
                  <Table.Th>Dodano</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {q.data.map((d) => (
                  <Table.Tr key={d.id}>
                    <Table.Td>
                      <Anchor href={`/api/fleet/documents/${d.id}/file`} size="sm" fw={500}>
                        {d.fileName}
                      </Anchor>
                      <Text size="xs" c="dimmed">
                        {[d.description, sizeText(d.size)].filter(Boolean).join(' · ')}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <Text size="sm">{VEHICLE_DOC_CATEGORIES[d.category]}</Text>
                        {d.sensitive && (
                          <Tooltip label="Dokument wrażliwy — widoczny tylko z uprawnieniem do kosztów">
                            <IconLock size={14} color="var(--mantine-color-dimmed)" />
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="xs">{formatDateTime(d.createdAt)}</Text>
                      <Text size="xs" c="dimmed">
                        {d.createdByName ?? '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">
                      <Group gap={4} justify="flex-end" wrap="nowrap">
                        <ActionIcon variant="subtle" component="a" href={`/api/fleet/documents/${d.id}/file`} aria-label="Pobierz">
                          <IconDownload size={16} />
                        </ActionIcon>
                        {v.can.manage && (
                          <ActionIcon variant="subtle" color="red" aria-label="Usuń dokument" onClick={async () => (await ask(`Plik „${d.fileName}” zniknie z karty pojazdu.`, { title: 'Usunąć dokument?', confirm: 'Usuń', danger: true })) && remove.mutate(d.id)}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        ) : (
          <EmptyState compact title="Brak dokumentów" hint="Dowód rejestracyjny, polisy, umowy leasingu, protokoły serwisu, zdjęcia." />
        )}
      </QueryState>
      <Modal opened={!!file} onClose={() => setFile(null)} title="Nowy dokument pojazdu">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            {file?.name}
          </Text>
          <Select label="Rodzaj" data={categories.map(([value, label]) => ({ value, label }))} value={category} onChange={(x) => x && setCategory(x as VehicleDocCategory)} allowDeselect={false} />
          {SENSITIVE_DOC_CATEGORIES.includes(category) && (
            <Badge color="gray" variant="light" leftSection={<IconLock size={12} />}>
              wrażliwy — tylko z uprawnieniem do kosztów
            </Badge>
          )}
          <TextInput label="Opis" value={description} onChange={(e) => setDescription(e.currentTarget.value)} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setFile(null)}>
              Anuluj
            </Button>
            <Button loading={upload.isPending} onClick={() => upload.mutate()}>
              Dodaj dokument
            </Button>
          </Group>
        </Stack>
      </Modal>
    </SectionCard>
  );
}

// ── historia zdarzeń ──

export function EventsTab({ v }: { v: VehicleDto }) {
  const q = useQuery({ queryKey: ['fleet', 'vehicle', v.id, 'events'], queryFn: () => api<FleetEventDto[]>(`/fleet/vehicles/${v.id}/events`) });
  return (
    <SectionCard title="Historia zdarzeń">
      <QueryState isLoading={q.isLoading} error={q.error}>
        {q.data?.length ? (
          <Timeline bulletSize={12} lineWidth={2}>
            {q.data.map((e, i) => (
              <Timeline.Item key={`${e.at}-${i}`} title={<Text size="sm">{e.text}</Text>}>
                <Text size="xs" c="dimmed">
                  {formatDateTime(e.at)}
                  {e.byName ? ` · ${e.byName}` : ''}
                </Text>
              </Timeline.Item>
            ))}
          </Timeline>
        ) : (
          <EmptyState compact title="Brak zdarzeń" />
        )}
      </QueryState>
    </SectionCard>
  );
}
